import { useEffect, useState } from "react";
import { StudentStatisticsQueryParams } from "../../types/Student";
import { useForm } from "react-hook-form";
import { useGetStudentStatistics } from "../../hooks/useGetStudentStatistics";
import { Card, Flex, Heading, Button } from "@radix-ui/themes";
import TableComponent from "../../components/common/Tables";
import SelectComponent from "../../components/inputs/Select/SelectComponent";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import Chart from 'react-apexcharts';
import { ApexOptions } from "apexcharts";
import { useGetFacultiesQuery } from "../../hooks/useGetFacultiesQuery";
import { useGetDepartamentsQuery } from "../../hooks/useGetDepartamentsQuery";
import { useGetKnowledgeDomainsQuery } from "../../hooks/useGetKnowledgeDomainsQuery";
import { useGetKnowledgeSubDomainsQuery } from "../../hooks/useGetKnowledgeSubDomainsQuery";
import { isEmpty } from "lodash";
import { CancelEnrollmentReasons, provinces, Regimes, StudentEletronicDevice, StudentSpecialConditions } from "../../utils/codes";
import { useGetAcademicYearQuery } from "../../hooks/useGetAcademicYearQuery";
import RadioGroupComponent from "../../components/inputs/RadioGroup/RadioGroupComponent";
import { useGetScholarshipTypesQuery } from "../../hooks/useGetScholarshipTypesQuery";
import { useGetScholarshipProvidersQuery } from "../../hooks/useQueryScholarshipProviders";
import { useGetCoursesQuery } from "../../hooks/useGetCoursesQuery";
import { useChartTheme } from '../../hooks/useChartTheme';

const columnNamesMap: { [key: string]: string } = {
    knowledgeDomain: "Domínio do Conhecimento",
    knowledgeSubDomain: "Subdomínio do Conhecimento",
    course: "Curso",
    duration: "Duração (anos)",
    level: "Nível",
    studentCount: "Total de Estudantes",
    newStudents: "Novos Estudantes",
    internStudents: "Estudantes Internos",
    graduatedStudents: "Estudantes Graduados"
};
const columnNamesMapTotal: { [key: string]: string } = {
    studentCount: "Total de Estudantes",
    newStudents: "Novos Estudantes",
    internStudents: "Estudantes Internos",
    graduatedStudents: "Estudantes Graduados"
};


export const StudentsStatistics = () => {
    const { withTheme } = useChartTheme();


    const { control, handleSubmit, watch, setValue, formState: { errors }, resetField, getValues } = useForm<StudentStatisticsQueryParams>({
        defaultValues: {},
    });
    const filters = watch();

    const { data: faculties, isLoading: isLoadingFaculties, isError: errorFaculties } = useGetFacultiesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: courses, isLoading: isLoadingCourses, isError: errorCourses } = useGetCoursesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: departaments, isLoading: isLoadingDepartaments, isError: errorDepartaments, refetch: refreshDepartaments } = useGetDepartamentsQuery(
        { PageNumber: 1, PageSize: 400, IdFaculty: getValues('idFaculty') },
        { enabled: !!getValues('idFaculty') }
    );
    const { data: scholarshipTypes, isLoading: isLoadingScholarshipTypes, isError: errorScholarshipTypes } = useGetScholarshipTypesQuery({ PageNumber: 1, PageSize: 400 });
    const { data: scholarshipProviders, isLoading: isLoadingScholarshipProviders, isError: errorScholarshipProviders } = useGetScholarshipProvidersQuery({ PageNumber: 1, PageSize: 400 });


    const { data: domains, isLoading: isLoadingDomains, isError: errorDomains } = useGetKnowledgeDomainsQuery({ PageNumber: 1, PageSize: 400 });
    const { data: subDomains, isLoading: isLoadingSubDomains, isError: errorSubDomains, refetch: refreshSubDomains } = useGetKnowledgeSubDomainsQuery(
        { PageNumber: 1, PageSize: 400, DomainId: getValues('KnowledgeDomain') },
        { enabled: !!getValues('KnowledgeDomain') }
    );
    const { data: academicYears, isLoading: isLoadingAcademicYears, isError: errorAcademicYears } = useGetAcademicYearQuery({ PageNumber: 1, PageSize: 400 });

    const { data, status } = useGetStudentStatistics({ ...filters } as any);
    const chartOptions: ApexOptions = {
        chart: { type: "bar", height: 350 },
        title: { text: "_____", align: "center" },
        xaxis: {
            categories: data && data.length > 0 ? data.map((stat: any) => stat?.course || "Sem dados") : ["Sem dados"],
            title: { text: "Curso" },
        },
        series: [
            {
                name: "Total de Estudantes",
                data: data && data.length > 0 ? data.map((stat: any) => stat?.studentCount || 0) : [0],
            },
        ],
    };

    const onSubmit = handleSubmit(async (data) => {
    });
    const handleKnowledgeDomainChange = (id: string) => {
        setValue('KnowledgeDomain', id);
        if (isEmpty(id)) {
            resetField('KnowledgeSubDomain')
            return;
        }
        refreshSubDomains();
    };
    useEffect(() => {
        if (academicYears?.items && academicYears.items.length > 0) {
            if (!getValues('IdAcademicYear')) {
                const mostRecentYear = academicYears.items[academicYears.items.length - 1];
                setValue('IdAcademicYear', mostRecentYear.id);
            }
        }
    }, [academicYears, setValue, getValues]);

    return (
        <Card>
            <Flex gap="4" direction="column">
                <Heading>Dados estatísticos - Ano {(academicYears?.items.find(i => i.id === watch('IdAcademicYear'))?.year) ?? 'Todos'}</Heading>

                <form onSubmit={onSubmit}>
                    <Flex gap="4" direction="column">
                        <Flex gap='2'>
                            <SelectComponent name="IdAcademicYear" label='Ano Académico' control={control} errors={errors} style={{ width: '35%' }}
                                options={academicYears?.items.map(ac => ({
                                    value: ac.id ?? "",
                                    label: ac.year ?? ""
                                }))}
                                itemsError={errorAcademicYears}
                                itemsLoading={isLoadingAcademicYears}
                            />

                            <SelectComponent
                                name="KnowledgeDomain"
                                label="Domínio de Conhecimento"
                                control={control}
                                errors={errors}
                                style={{ width: '100%' }}
                                options={domains?.items.map(domain => ({
                                    value: domain.id ?? "",
                                    label: domain.description ?? ""
                                }))}
                                itemsLoading={isLoadingDomains}
                                itemsError={errorDomains}
                                onSelect={handleKnowledgeDomainChange}

                            />
                            <SelectComponent
                                name="KnowledgeSubDomain"
                                label="SubDomínio de Conhecimento"
                                control={control}
                                errors={errors}
                                style={{ width: '100%' }}
                                options={subDomains?.items.map(domain => ({
                                    value: domain.id ?? "",
                                    label: domain.description ?? ""
                                }))}
                                itemsLoading={isLoadingSubDomains}
                                itemsError={errorSubDomains}
                            />
                        </Flex>
                        <Flex gap="4" direction={'column'}>
                            <SelectComponent
                                name="Course"
                                label="Cursos"
                                control={control}
                                errors={errors}
                                style={{ width: '100%' }}
                                options={courses?.items.map(domain => ({
                                    value: domain.id ?? "",
                                    label: domain.description ?? ""
                                }))}
                                itemsLoading={isLoadingCourses}
                                itemsError={errorCourses}
                            />
                            <RadioGroupComponent name="Regime" label="Regime" control={control} errors={errors} style={{ width: '100%' }}
                                options={Regimes.map(r => ({
                                    value: r.id ?? "",
                                    label: r.description ?? ""
                                }))}
                                fieldProps={{
                                    style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
                                }}
                            />
                            <TextFieldComponent name="YearSubscription" style={{ width: '100%' }}
                                label="Nível" control={control} errors={errors} fieldProps={{ placeholder: "Nível" }} />
                            <TextFieldComponent name="SemesterSubscription" style={{ width: '100%' }}
                                label="Semestre" control={control} errors={errors} fieldProps={{ placeholder: "Semestre" }} />
                        </Flex>
                        <Flex gap="4">
                            <TextFieldComponent name="MinAge" style={{ width: '100%' }}
                                label="Idade Mínima" control={control} errors={errors} fieldProps={{ placeholder: "Idade mínima", type: "number" }} />
                            <TextFieldComponent name="MaxAge" style={{ width: '100%' }}
                                label="Idade Máxima" control={control} errors={errors} fieldProps={{ placeholder: "Idade máxima", type: "number" }} />
                            <RadioGroupComponent name="Gender" label="Género" control={control} errors={errors} style={{ width: '100%' }}
                                options={[
                                    { value: "M", label: "Masculino" }
                                    , { value: "F", label: "Feminino" }]}
                                fieldProps={{
                                    style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' }
                                }}
                            />
                        </Flex>
                        <Flex gap="4">
                            <RadioGroupComponent name="IsForeign" label="Estrangeiro" control={control} errors={errors}
                                style={{ width: '50%' }}
                                options={[
                                    { value: "true", label: "Sim" }
                                    , { value: "false", label: "Não" }]}
                                fieldProps={{
                                    style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' },
                                    // onChange: handleOnChangeIsForeign
                                }}
                            />

                            <SelectComponent
                                name="HasElectronicDevice"
                                label="Dispositivo electrónico "
                                control={control}
                                errors={errors}
                                composedName={{ parent: "student", prop: 'eletronicDevice' }}
                                options={StudentEletronicDevice?.map((country: { id: any, description: any }) => ({
                                    value: `${country.id}`,
                                    label: country.description ?? "",
                                }))}
                                style={{ width: '100%' }}
                            />
                            <SelectComponent
                                name="SpecialConditions"
                                label="Necessidades especiais"
                                control={control}
                                errors={errors}
                                composedName={{ parent: "student", prop: 'specialConditions' }}
                                options={StudentSpecialConditions?.map((country: { id: any, description: any }) => ({
                                    value: country.id,
                                    label: country.description ?? "",
                                }))}
                                style={{ width: '100%' }}
                            />
                        </Flex >
                        <Flex gap="4">
                            <SelectComponent
                                name="ProvinceGraduated12orEqual"
                                label="Província completou á 12.ª Classe ou equivalente"
                                control={control}
                                errors={errors}
                                composedName={{ parent: "student", prop: 'province' }}
                                options={provinces?.map((p: { id: any, description: any }) => ({
                                    value: p.id,
                                    label: p.description ?? "",
                                }))}
                                style={{ width: '100%' }}
                            />
                            <SelectComponent
                                name="ProvinceOrigin"
                                label="Natural de"
                                control={control}
                                errors={errors}
                                composedName={{ parent: "student", prop: 'province' }}
                                options={provinces?.map((p: { id: any, description: any }) => ({
                                    value: p.id,
                                    label: p.description ?? "",
                                }))}
                                style={{ width: '100%' }}
                            />
                        </Flex>
                        <Flex>
                            <RadioGroupComponent name="IsCanceled" label="Anulou matrícula?" control={control} errors={errors}
                                style={{ width: '50%' }}
                                options={[
                                    { value: "true", label: "Sim" }
                                    , { value: "false", label: "Não" }]}
                                fieldProps={{
                                    style: { display: 'flex', flexDirection: 'row', gap: 'var(--space-4)' },
                                }}
                            />

                            <SelectComponent
                                name="IsCanceledReason"
                                label="Motivo da anulação de matrícula?"
                                control={control}
                                errors={errors}
                                options={CancelEnrollmentReasons?.map((c: { id: any, description: any }) => ({
                                    value: c.id,
                                    label: c.description ?? "",
                                }))}
                                style={{ width: '100%' }}
                            />
                        </Flex>
                        <Flex gap="4">
                            <SelectComponent
                                name="ScholarshipType"
                                label="Tipo de Bolsa"
                                control={control}
                                errors={errors}
                                style={{ width: '100%' }}
                                options={scholarshipTypes?.items.map(t => ({
                                    value: t.id ?? "",
                                    label: t.description ?? ""
                                }))}
                                itemsLoading={isLoadingScholarshipTypes}
                                itemsError={errorScholarshipTypes}
                            />
                            <SelectComponent
                                name="ScholarshipProvider"
                                label="Provedor/Financiador"
                                control={control}
                                errors={errors}
                                style={{ width: '100%' }}
                                options={scholarshipProviders?.items.map(t => ({
                                    value: t.id ?? "",
                                    label: t.description ?? ""
                                }))}
                                itemsLoading={isLoadingScholarshipProviders}
                                itemsError={errorScholarshipProviders}
                            />
                        </Flex>
                    </Flex>
                </form>
                <TableComponent
                    columns={Object.keys(columnNamesMap)}
                    items={data || []}
                    columnNamesMap={columnNamesMap}
                    totalPages={1}
                    currentPage={1}
                    onPageChange={() => { }}
                    totalColumns={Object.keys(columnNamesMapTotal)}
                />
                {data && data?.length > 0 && <Chart options={withTheme(chartOptions)} series={chartOptions?.series} type="bar" height={350} />}
            </Flex>
        </Card>
    );
}
